import React, { useState, useEffect } from "react";
import { Eye, EyeOff, Mail, Lock, ArrowRight, AlertCircle, CheckCircle2 } from "lucide-react";
import { businessLogin, login } from "../ApiCalls/authentication";
import { useNavigate } from "react-router-dom";
import { brand } from "../config";
import mantisLogo from "../Assets/img/mantisBlackLogo.png";
import sandyLogo from "../Assets/img/sandyBlackLogo.png";
import { connect } from "react-redux";
import { setInStorage, getFromStorage } from "../utils/storage";
import {
  userLogin,
  setUser,
  setToken,
  setUserId,
  setIsBusiness,
  setMantisBusiness,
} from "../Redux/modules/auth";
import { sendRecoverPasswordEmail } from "../ApiCalls/mantisBusiness";

function mapDispatchToProps(dispatch) {
  return {
    userLogin: () => dispatch(userLogin()),
    setUser: (user) => dispatch(setUser(user)),
    setUserId: (userId) => dispatch(setUserId(userId)),
    setToken: (token) => dispatch(setToken(token)),
    setIsBusiness: (isBusiness) => dispatch(setIsBusiness(isBusiness)),
    setMantisBusiness: (mantisBusiness) =>
      dispatch(setMantisBusiness(mantisBusiness)),
  };
}

const select = (state) => {
  return {
    user: state.auth.user,
  };
};

function BusinessLogin(props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [showSessionExpiredToast, setShowSessionExpiredToast] = useState(false);
  const [showLoginRequiredToast, setShowLoginRequiredToast] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [loginRequiredToastVisible, setLoginRequiredToastVisible] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  function showToast() {
    setShowSessionExpiredToast(true);
    setToastVisible(true);

    setTimeout(() => {
      setToastVisible(false);
      setTimeout(() => {
        setShowSessionExpiredToast(false);
      }, 500);
    }, 3000);
  }

  function handleShowLoginRequiredToast() {
    setShowLoginRequiredToast(true);
    setLoginRequiredToastVisible(true);

    setTimeout(() => {
      setLoginRequiredToastVisible(false);
      setTimeout(() => {
        setShowLoginRequiredToast(false);
      }, 500);
    }, 4000);
  }

  useEffect(() => {
    const sessionExpired = localStorage.getItem("sessionExpired");
    const loginRequired = localStorage.getItem("mantis_login_required");

    if (sessionExpired) {
      showToast();
      localStorage.removeItem("sessionExpired");
    }

    if (loginRequired) {
      handleShowLoginRequiredToast();
      localStorage.removeItem("mantis_login_required");
    }
  }, []);

  async function submitLogin(event) {
    if (!loggingIn) {
      event.preventDefault();
      event.stopPropagation();
      setLoggingIn(true);
      const loginRes = await businessLogin(email, password);
      if (loginRes.success) {
        setInStorage("mantis_app", { token: loginRes.token });
        await updateUserSession(
          loginRes.userId,
          loginRes.user,
          loginRes.token,
          loginRes.mantisBusiness
        );
      } else {
        alert("Credenciales incorrectas");
      }
      setLoggingIn(false);
    }
  }

  async function handleSendRecoverPasswordMail() {
    sendRecoverPasswordEmail(forgotPasswordEmail);
  }

  function updateUserSession(userId, user, token, mantisBusiness) {
    props.setUser({ user });
    props.setToken({ token });
    props.setUserId({ userId });
    props.setMantisBusiness({ mantisBusiness });
    props.userLogin();
    props.setIsBusiness({ isBusiness: true });
    
    // Verificar si hay una URL de redirección guardada
    const redirectUrl = localStorage.getItem('mantis_redirect_url');
    if (redirectUrl) {
      localStorage.removeItem('mantis_redirect_url');
      navigate(redirectUrl);
    } else {
    navigate("/business/backoffice");
    }
  }

  function closeForgotPasswordModal() {
    setShowForgotPasswordModal(false);
    setForgotPasswordEmail("");
  }

  return (
    <>
      {/* Custom Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .bg-grid-slate-100 {
            background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32' width='32' height='32' fill='none' stroke='rgb(148 163 184 / 0.05)'%3e%3cpath d='m0 .5h32M.5 0v32'/%3e%3c/svg%3e");
          }
          .animate-in {
            animation: slideIn 0.3s ease-out;
          }
          .fade-in {
            animation: fadeIn 0.3s ease-out;
          }
          .slide-in-from-bottom-4 {
            animation: slideInFromBottom 0.3s ease-out;
          }
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideInFromBottom {
            from { opacity: 0; transform: translateY(16px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `
      }} />
      
      <div className="min-h-screen bg-white flex items-center justify-center p-4 relative overflow-hidden">
        {/* Tech Background Effects */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-orange-200/15 via-amber-100/8 to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-orange-300/12 via-transparent to-transparent"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#f97316_1px,transparent_1px),linear-gradient(to_bottom,#f97316_1px,transparent_1px)] bg-[size:24px_24px] opacity-[0.02]"></div>
        
        {/* Floating Tech Elements */}
        <div className="absolute top-20 left-20 w-2 h-2 bg-orange-400 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-40 right-32 w-1 h-1 bg-amber-400 rounded-full opacity-25 animate-pulse delay-1000"></div>
        <div className="absolute bottom-32 left-40 w-1.5 h-1.5 bg-orange-300 rounded-full opacity-30 animate-pulse delay-500"></div>
        <div className="absolute bottom-20 right-20 w-1 h-1 bg-amber-400 rounded-full opacity-25 animate-pulse delay-700"></div>
      
      {/* Main Container */}
      <div className="relative w-full max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center min-h-0">
          
          {/* Left Side - Logo & Branding */}
          <div className="hidden lg:flex flex-col items-center justify-center space-y-8 min-h-[500px]">
            <div className="relative">
              {/* Tech Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full blur-3xl opacity-12 scale-150 animate-pulse"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-amber-400 rounded-full blur-2xl opacity-10 scale-125"></div>
              
              {/* Logo */}
              <img
                src={brand === "MANTIS" ? mantisLogo : sandyLogo}
                alt="Logo"
                className="relative w-48 h-auto object-contain drop-shadow-[0_25px_25px_rgba(0,0,0,0.15)]"
              />
              
              {/* Tech Elements around logo */}
              <div className="absolute -top-4 -right-4 w-3 h-3 bg-orange-500 rounded-full opacity-50 animate-ping"></div>
              <div className="absolute -bottom-6 -left-6 w-2 h-2 bg-amber-500 rounded-full opacity-45 animate-pulse delay-300"></div>
            </div>
            
            <div className="text-center space-y-4">
              <h2 className="text-3xl font-bold text-slate-800 drop-shadow-lg">
                Bienvenido de vuelta
              </h2>
              
              {/* Tech Badge */}
              {/* <div className="inline-flex items-center px-4 py-2 bg-orange-500/10 backdrop-blur-sm rounded-full border border-orange-400/30">
                <div className="w-2 h-2 bg-orange-500 rounded-full mr-2 animate-pulse"></div>
                <span className="text-orange-700 text-sm font-medium">Sistema Empresarial Avanzado</span>
              </div> */}
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="w-full max-w-md mx-auto lg:mx-0 relative">
            {/* Decorative Elements around Login Card */}
            <div className="absolute -top-8 -right-8 w-16 h-16 bg-orange-500/10 rounded-full blur-xl"></div>
            <div className="absolute -bottom-8 -left-8 w-12 h-12 bg-amber-500/10 rounded-full blur-lg"></div>
            <div className="absolute top-1/2 -right-12 w-8 h-8 bg-orange-300/15 rounded-full blur-md"></div>
            <div className="absolute top-1/4 -left-12 w-6 h-6 bg-amber-300/15 rounded-full blur-sm"></div>
            
            {/* Mobile Logo */}
            <div className="lg:hidden flex justify-center mb-6">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full blur-2xl opacity-10 scale-125 animate-pulse"></div>
          <img
            src={brand === "MANTIS" ? mantisLogo : sandyLogo}
                  alt="Logo"
                  className="relative w-24 sm:w-32 h-auto object-contain drop-shadow-[0_20px_20px_rgba(0,0,0,0.1)]"
                />
                <div className="absolute -top-2 -right-2 w-2 h-2 bg-orange-500 rounded-full opacity-50 animate-ping"></div>
              </div>
            </div>

                        {/* Login Card */}
            <div className="bg-white backdrop-blur-lg rounded-2xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.15)] border border-orange-200 p-6 sm:p-8 relative overflow-hidden ring-1 ring-orange-100/50">
              {/* Tech Border Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500/8 via-transparent to-amber-500/8 rounded-2xl"></div>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-amber-500"></div>
              
              <div className="relative space-y-6">
                {/* Header */}
                <div className="text-center space-y-2">
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Iniciar Sesión</h1>
                </div>

                {/* Form */}
                <form onSubmit={submitLogin} className="space-y-4">
                  {/* Email Field */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Correo Electrónico
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-orange-400" />
                      <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                        placeholder="tu@empresa.com"
                        className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500 hover:border-orange-300 transition-all duration-200 text-slate-900 placeholder-slate-400 bg-white"
                        disabled={loggingIn}
                        required
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Contraseña
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-orange-400" />
                      <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full pl-10 pr-12 py-3 border border-slate-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500 hover:border-orange-300 transition-all duration-200 text-slate-900 placeholder-slate-400 bg-white"
                        disabled={loggingIn}
                        required
                />
                      <button
                        type="button"
                  onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-orange-400 hover:text-orange-600 transition-colors"
                        disabled={loggingIn}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Forgot Password Link */}
                  <div className="text-right">
                    <button
                      type="button"
                onClick={() => setShowForgotPasswordModal(true)}
                      className="text-sm text-orange-600 hover:text-orange-700 font-medium transition-colors"
                      disabled={loggingIn}
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                  </div>

                  {/* Login Button */}
                  <button
                    type="submit"
                    disabled={loggingIn || !email || !password}
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3 px-4 rounded font-medium hover:from-orange-600 hover:to-amber-600 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
                  >
                    {loggingIn ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <span>Ingresar</span>
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>

                  {/* Register Link */}
                  <div className="text-center pt-4 border-t border-orange-200">
                    <p className="text-sm text-slate-600">
                      ¿No tienes cuenta?{" "}
                      <button
                        type="button"
                  onClick={() => navigate("/businessRegister")}
                        className="text-orange-600 hover:text-orange-700 font-medium transition-colors"
                        disabled={loggingIn}
                      >
                        Regístrate aquí
                      </button>
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPasswordModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.2)] max-w-md w-full p-6 animate-in fade-in slide-in-from-bottom-4 duration-300 border border-orange-200 ring-1 ring-orange-100/50">
            <div className="space-y-4">
              <div className="text-center space-y-2">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full flex items-center justify-center mx-auto">
                  <Mail className="h-6 w-6 text-orange-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900">Recuperar Contraseña</h2>
                <p className="text-slate-600">Ingresa tu correo y te enviaremos las instrucciones</p>
              </div>

              <form onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (forgotPasswordEmail !== "") {
                closeForgotPasswordModal();
                handleSendRecoverPasswordMail();
                  // TODO: Replace alert with proper toast
                alert("Se ha enviado un correo para recuperar tu contraseña");
              }
              }} className="space-y-4">
                                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                Correo Electrónico
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-orange-400" />
                      <input
                type="email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        placeholder="tu@empresa.com"
                        className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500 hover:border-orange-300 transition-all duration-200 bg-white"
                        required
                      />
                    </div>
                  </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeForgotPasswordModal}
                    className="flex-1 px-4 py-2 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded font-medium transition-colors shadow-lg"
                  >
              Enviar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      {showSessionExpiredToast && (
        <div className={`fixed top-4 right-4 bg-white border-l-4 border-red-500 rounded shadow-lg p-4 max-w-md z-50 transition-all duration-300 ${toastVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}>
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-slate-900">Sesión Expirada</p>
              <p className="text-sm text-slate-600">Tu sesión ha expirado. Por favor, inicia sesión nuevamente.</p>
            </div>
          </div>
        </div>
      )}

      {showLoginRequiredToast && (
        <div className={`fixed top-4 right-4 bg-white border-l-4 border-orange-500 rounded shadow-lg p-4 max-w-md z-50 transition-all duration-300 ${loginRequiredToastVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}>
          <div className="flex items-start space-x-3">
            <CheckCircle2 className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-slate-900">Login Requerido</p>
              <p className="text-sm text-slate-600">🔐 Necesitas iniciar sesión para acceder a esa página. Después del login serás redirigido automáticamente.</p>
            </div>
          </div>
                 </div>
      )}
    </div>
    </>
  );
}

export default connect(select, mapDispatchToProps)(BusinessLogin);